import { useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Add, MoreHoriz, Search } from '@mui/icons-material';
import { Box, IconButton, Typography, Tooltip, TextField } from '@mui/material';
import { toast } from 'react-toastify';
import { useStyles } from '../styles/sectionStyles';
import { SectionActionPopup } from './popups';
import { deleteSection, updateSection } from '../../../../services/sectionService';
import { useProject } from '../../Layout/header/context';
import { isAdmin, isClient, isStakeholder } from '../../../../utils/share';
import BoardSearchField from '../../../../components/board-search';

const SectionHeader = ({
  section,
  onAddClick,
  refetchSection,
  dragHandleProps,
  sectionQuery,
  onSectionQueryChange,
  visibleCount,
  totalCount,
  isFiltering,
}) => {
  const classes = useStyles();
  const [openAction, setOpenAction] = useState(null);
  const [openRename, setOpenRename] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [disabledRename, setDisabledRename] = useState(false);
  const renameInputRef = useRef(null);

  const { projectDetails, isEditPermission , projectRole } = useProject();

  const handleOpenAction = useCallback((event) => setOpenAction(event.currentTarget), []);
  const handleCloseAction = useCallback(() => setOpenAction(null), []);

  const handleToggleSearch = useCallback(() => {
    setShowSearch((prev) => {
      // Closing the field drops its query, so a column is never left silently filtered.
      if (prev) onSectionQueryChange('');
      return !prev;
    });
  }, [onSectionQueryChange]);

  const handleOpenRename = useCallback(() => {
    setOpenRename(true);
    setOpenAction(null);
  }, []);
  const handleCloseRename = useCallback(() => setOpenRename(false), []);

  const handleRenameSubmit = useCallback(async () => {
    try {
      const name = renameInputRef.current?.value?.trim();

      if (name?.length > 0 && name !== section?.name) {
        // save the section
        setDisabledRename(true);
        await updateSection(section?.id, { name }); // success message: response.data.message
        refetchSection();
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || 'An error occurred while saving the section. Please try again later.';
      toast.error(errMsg);
    } finally {
      handleCloseRename();
      setDisabledRename(false);
    }
  }, [section?.name, section?.id, handleCloseRename, refetchSection]);

  const handleKeyDown = useCallback(
    (event) => {
      // When the user presses Esc key
      if (event.keyCode === 27) handleCloseRename();

      // When the user presses Enter key
      if (event.keyCode === 13) handleRenameSubmit();
    },
    [handleCloseRename, handleRenameSubmit]
  );

  const onDelete = useCallback(async () => {
    try {
      await deleteSection(section?.id); // success message: response.data.message
      refetchSection();
      toast.success(`${section?.name} deleted`);
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || 'An error occurred while deleting the section. Please try again later.';
      toast.error(errMsg);
    } finally {
      handleCloseAction();
    }
  }, [section?.id, section?.name, refetchSection, handleCloseAction]);

  const taskCount = section?.tasks?.length || 0;

  if (openRename)
    return (
        <TextField
        onBlur={handleRenameSubmit}
        autoFocus
        placeholder="Enter section name"
        defaultValue={section?.name}
        size="small"
        className={classes?.headerField}
        onKeyDown={handleKeyDown}
        inputRef={renameInputRef}
        disabled={disabledRename}
      />
    );

  const createTaskDisabled = projectDetails?.allowManualTask === false && !isEditPermission;
  const canAddTask = isEditPermission || isAdmin() || isStakeholder() || isClient() || projectDetails?.allowManualTask || projectRole !== "Team Members"
  return (
    <>
      <header className={classes.header} {...dragHandleProps}>
      <Box className={classes.titleBox}>
        <Tooltip title={section?.name} placement="top" arrow>
          <Typography variant="h6" textTransform="capitalize" noWrap onClick={!createTaskDisabled && handleOpenRename}>
            {section?.name}
          </Typography>
        </Tooltip>
        <Tooltip
          title={
            isFiltering
              ? `${visibleCount} of ${totalCount} task${totalCount === 1 ? '' : 's'} match`
              : `${taskCount} task${taskCount === 1 ? '' : 's'} in this section`
          }
          placement="top"
          arrow
        >
          <Typography variant="overline" sx={{ height: 'auto', cursor: 'default' }}>
            {isFiltering ? `${visibleCount} of ${totalCount}` : taskCount}
          </Typography>
        </Tooltip>
      </Box>
      <Box className={classes.actionBox}>
        <Tooltip title="Search tasks in this section" placement="top" arrow>
          <IconButton size="small" onClick={handleToggleSearch}>
            <Search fontSize="small" />
          </IconButton>
        </Tooltip>
        {!createTaskDisabled && (
          <>
            {canAddTask && (
              <Tooltip title={isStakeholder() ? 'Add Issue' : 'Add Task'} placement="top" arrow>
                <IconButton size="small" onClick={onAddClick}>
                  <Add fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="More Actions" placement="top" arrow>
              <IconButton size="small" onClick={handleOpenAction}>
                <MoreHoriz disabled={isAdmin()} fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
        <SectionActionPopup
          open={Boolean(openAction)}
          anchorEl={openAction}
          onClose={handleCloseAction}
          taskCount={taskCount}
          onDelete={onDelete}
          onRename={handleOpenRename}
        />
      </header>
      {showSearch && (
        <BoardSearchField
          compact
          autoFocus
          value={sectionQuery}
          onChange={onSectionQueryChange}
          onClose={handleToggleSearch}
          placeholder="Search in this section"
        />
      )}
    </>
  );
};

SectionHeader.propTypes = {
  section: PropTypes.object,
  onAddClick: PropTypes.func,
  refetchSection: PropTypes.func,
  dragHandleProps: PropTypes.object,
  sectionQuery: PropTypes.string,
  onSectionQueryChange: PropTypes.func,
  visibleCount: PropTypes.number,
  totalCount: PropTypes.number,
  isFiltering: PropTypes.bool,
};

export default SectionHeader;
